import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Grid,
  Box,
  Divider,
  CircularProgress,
  Slide,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SystemUpdateIcon from "@mui/icons-material/SystemUpdate";
import AppsIcon from "@mui/icons-material/Apps";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import { userDetailsApi, manualUpdateApi, deleteOnePlanApi } from "../../api/adminApi";
import { isEmailAllowedForMembers } from "../../utils/auth";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

export default function UserPlansDialog({ open, onClose, userMobile }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [newPlanName, setNewPlanName] = useState("Silver");
  const [newPlanAmount, setNewPlanAmount] = useState(299);

  const serializedObject = sessionStorage?.getItem("userDetails");
  const userDetails = serializedObject ? JSON.parse(serializedObject) : null;
  const adminEmail = userDetails?.email || "";
  const isDeveloper = isEmailAllowedForMembers(adminEmail);

  const handlePlanNameChange = (e) => {
    const val = e.target.value;
    setNewPlanName(val);
    if (val === "Silver") setNewPlanAmount(299);
    else if (val === "Gold") setNewPlanAmount(499);
    else if (val === "Platinum") setNewPlanAmount(799);
    else setNewPlanAmount(0);
  };

  const handleDeletePlan = async () => {
    if (!window.confirm("Are you sure you want to delete this user's latest plan? This cannot be undone.")) return;
    setActionLoading(true);
    const res = await deleteOnePlanApi(userMobile);
    if (res && res.success) {
      alert("Plan deleted successfully!");
      // Reload user details
      const freshRes = await userDetailsApi(userMobile);
      if (freshRes && freshRes.success) {
        setData(freshRes.data);
      }
    } else {
      alert("Failed to delete plan: " + (res?.message || "Unknown error"));
    }
    setActionLoading(false);
  };

  const handleAddPlan = async (e) => {
    e.preventDefault();
    if (newPlanAmount === "" || isNaN(newPlanAmount) || newPlanAmount < 0) {
      alert("Please enter a valid plan amount greater than or equal to 0.");
      return;
    }
    setActionLoading(true);
    const res = await manualUpdateApi(userMobile, newPlanName, newPlanAmount);
    if (res && res.success) {
      alert("Plan added/updated successfully!");
      setShowAddForm(false);
      // Reload user details
      const freshRes = await userDetailsApi(userMobile);
      if (freshRes && freshRes.success) {
        setData(freshRes.data);
      }
    } else {
      alert("Failed to add/update plan: " + (res?.message || "Unknown error"));
    }
    setActionLoading(false);
  };

  useEffect(() => {
    if (open && userMobile) {
      setLoading(true);
      userDetailsApi(userMobile)
        .then((res) => {
          if (res && res.success) {
            setData(res.data);
          } else {
            setData(null);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
    }
  }, [open, userMobile]);

  const parsePlanDate = (dateVal) => {
    if (!dateVal || dateVal === "-") return 0;
    if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? 0 : dateVal.getTime();
    if (typeof dateVal === "number") return isNaN(dateVal) ? 0 : dateVal;
    if (typeof dateVal !== "string") return 0;

    const str = dateVal.trim();
    const dmyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmyMatch) {
      const day = parseInt(dmyMatch[1], 10);
      const month = parseInt(dmyMatch[2], 10) - 1;
      const year = parseInt(dmyMatch[3], 10);
      const d = new Date(year, month, day);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }

    const d = new Date(str);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "-") return "-";
    const ts = parsePlanDate(dateStr);
    if (ts > 0) {
      return new Date(ts).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    return String(dateStr);
  };

  const getPlanTime = (plan) => {
    return parsePlanDate(plan.subscribedDate || plan.createdAt || plan.startdate);
  };

  const getStatusStyle = (status, startdate, enddate) => {
    const s = String(status || "").trim().toLowerCase();
    const now = new Date();
    const start = startdate ? new Date(startdate) : null;
    const end = enddate ? new Date(enddate) : null;

    if (end && !isNaN(end.getTime()) && now > end) {
      return { label: "Expired", bg: "rgba(255, 96, 96, 0.1)", border: "1px solid rgba(255, 96, 96, 0.2)", color: "#FF6060" };
    }
    if (start && !isNaN(start.getTime()) && now < start && (s === "active" || s === "upcoming")) {
      return { label: "Upcoming", bg: "rgba(74, 144, 226, 0.1)", border: "1px solid rgba(74, 144, 226, 0.2)", color: "#4A90E2" };
    }
    if (s === "active") {
      return { label: "Active", bg: "rgba(49, 228, 168, 0.1)", border: "1px solid rgba(49, 228, 168, 0.2)", color: "#31E4A8" };
    }
    if (s === "upcoming") {
      return { label: "Upcoming", bg: "rgba(74, 144, 226, 0.1)", border: "1px solid rgba(74, 144, 226, 0.2)", color: "#4A90E2" };
    }
    return { label: status || "Inactive", bg: "rgba(166, 164, 164, 0.1)", border: "1px solid rgba(166, 164, 164, 0.2)", color: "#A6A4A4" };
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          position: "fixed",
          right: 0,
          margin: 0,
          height: "100%",
          maxHeight: "100%",
          borderRadius: "16px 0 0 16px",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: "#FCF8FF", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#6A5AE0", fontFamily: "Open Sans" }}>
          User Account Details
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, bgcolor: "#FAF2FF" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
            <CircularProgress sx={{ color: "#6A5AE0" }} />
          </Box>
        ) : data ? (
          <Grid container spacing={3}>
            {/* User Info Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  bgcolor: "white",
                  boxShadow: "0px 4px 20px rgba(106, 90, 224, 0.05)",
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, color: "#2B2A35", fontFamily: "Open Sans" }}>
                  {data.userDetails?.username || "N/A"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailIcon sx={{ color: "#8E8D9A" }} />
                      <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "14px" }}>
                        {data.userDetails?.email || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneIcon sx={{ color: "#8E8D9A" }} />
                      <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "14px" }}>
                        +{data.userDetails?.mobile || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CalendarMonthIcon sx={{ color: "#8E8D9A" }} />
                      <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "14px" }}>
                        DOB: {formatDate(data.userDetails?.DateOfBirth)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <BusinessIcon sx={{ color: "#8E8D9A" }} />
                      <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "14px" }}>
                        Company: {data.userDetails?.CompanyName || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <HomeIcon sx={{ color: "#8E8D9A" }} />
                      <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "14px" }}>
                        Address: {data.userDetails?.Address || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InfoIcon sx={{ color: "#8E8D9A" }} />
                      <Typography sx={{ color: "#8E8D9A", fontFamily: "Open Sans", fontSize: "12px" }}>
                        Registered Date: {formatDate(data.userDetails?.RegisteredDate || data.userDetails?.todayDate || data.userDetails?.createdAt)} | Platform: {data.userDetails?.AppName || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Hide / Show Toggle for Additional Device & App Details */}
                <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px dashed #E0D7F5" }}>
                  <Button
                    onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
                    endIcon={showAdditionalDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{
                      color: "#6A5AE0",
                      fontWeight: "bold",
                      fontFamily: "Open Sans",
                      fontSize: "13px",
                      textTransform: "none",
                      p: 0,
                      '&:hover': { bgcolor: "transparent", textDecoration: "underline" }
                    }}
                  >
                    {showAdditionalDetails ? "Hide Additional Device Details" : "View Additional Device Details"}
                  </Button>

                  <Collapse in={showAdditionalDetails}>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: "10px",
                        bgcolor: "#FCF8FF",
                        border: "1px solid #F0E7FF",
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <AppsIcon sx={{ color: "#6A5AE0", fontSize: "18px" }} />
                            <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "13px" }}>
                              <strong>App Source:</strong> {data.userDetails?.AppName || "N/A"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <SystemUpdateIcon sx={{ color: "#6A5AE0", fontSize: "18px" }} />
                            <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "13px" }}>
                              <strong>App Version:</strong> {data.userDetails?.AppVersion || data.userDetails?.DeviceVersion || "N/A"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PhoneIphoneIcon sx={{ color: "#6A5AE0", fontSize: "18px" }} />
                            <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "13px" }}>
                              <strong>Device Model:</strong> {data.userDetails?.DeviceName || "N/A"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <InfoIcon sx={{ color: "#6A5AE0", fontSize: "18px" }} />
                            <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "13px" }}>
                              <strong>OS Platform:</strong> {data.userDetails?.Os || "N/A"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <VerifiedUserIcon sx={{ color: "#6A5AE0", fontSize: "18px" }} />
                            <Typography sx={{ color: "#4E4D59", fontFamily: "Open Sans", fontSize: "13px" }}>
                              <strong>Device Verified:</strong> {data.userDetails?.isDeviceVerified ? "Yes" : "No"}
                            </Typography>
                          </Box>
                        </Grid>
                        {data.userDetails?.DeviceId && (
                          <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <InfoIcon sx={{ color: "#8E8D9A", fontSize: "16px" }} />
                              <Typography sx={{ color: "#8E8D9A", fontFamily: "Open Sans", fontSize: "12px", wordBreak: "break-all" }}>
                                <strong>Device ID:</strong> {data.userDetails?.DeviceId}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Grid>

            {isDeveloper && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    bgcolor: "white",
                    boxShadow: "0px 4px 20px rgba(106, 90, 224, 0.05)",
                    border: "1px solid rgba(106, 90, 224, 0.2)",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#6A5AE0", fontFamily: "Open Sans" }}>
                    Developer Operations
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mb: showAddForm ? 3 : 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowAddForm(!showAddForm)}
                      disabled={actionLoading}
                      sx={{ borderRadius: "8px", bgcolor: "#6A5AE0", color: "white", '&:hover': { bgcolor: "#5748c9" } }}
                    >
                      {showAddForm ? "Cancel Add Plan" : "Add Manual Plan"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDeletePlan}
                      disabled={actionLoading || !data?.userPlans || data.userPlans.length === 0}
                      sx={{ borderRadius: "8px" }}
                    >
                      Delete Last Plan
                    </Button>
                    {actionLoading && <CircularProgress size={24} sx={{ alignSelf: "center", color: "#6A5AE0" }} />}
                  </Box>

                  {showAddForm && (
                    <Box component="form" onSubmit={handleAddPlan} sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="plan-name-label">Plan Name</InputLabel>
                            <Select
                              labelId="plan-name-label"
                              value={newPlanName}
                              label="Plan Name"
                              onChange={handlePlanNameChange}
                            >
                              <MenuItem value="Silver">Silver</MenuItem>
                              <MenuItem value="Gold">Gold</MenuItem>
                              <MenuItem value="Platinum">Platinum</MenuItem>
                              <MenuItem value="Trial">Trial</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Plan Amount"
                            type="number"
                            size="small"
                            fullWidth
                            disabled
                            value={newPlanAmount}
                            onChange={(e) => setNewPlanAmount(Number(e.target.value))}
                          />
                        </Grid>
                      </Grid>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={actionLoading}
                        sx={{ mt: 1, alignSelf: "flex-start", bgcolor: "#6A5AE0", color: "white", borderRadius: "8px", '&:hover': { bgcolor: "#5748c9" } }}
                      >
                        Submit Manual Plan
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            {/* Plans Timeline Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  bgcolor: "white",
                  boxShadow: "0px 4px 20px rgba(106, 90, 224, 0.05)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3, color: "#2B2A35", fontFamily: "Open Sans" }}>
                  Plan Subscription History
                </Typography>
                
                {(!data.userPlans || data.userPlans.length === 0) ? (
                  <Typography sx={{ textAlign: "center", color: "#8E8D9A", py: 4 }}>
                    No subscription plans found for this user.
                  </Typography>
                ) : (
                  <Timeline position="right" sx={{ p: 0 }}>
                    {[...data.userPlans]
                      .sort((a, b) => getPlanTime(b) - getPlanTime(a))
                      .map((plan, idx) => {
                        const style = getStatusStyle(plan.planstatus, plan.startdate, plan.enddate);
                        return (
                          <TimelineItem key={plan._id || idx}>
                            <TimelineOppositeContent
                              sx={{ m: "auto 0", flex: 0.25, textAlign: "left", pl: 0 }}
                              align="right"
                              variant="body2"
                              color="text.secondary"
                            >
                              <Typography sx={{ fontWeight: "bold", fontSize: "13px", color: "#6A5AE0" }}>
                                {formatDate(plan.subscribedDate || plan.createdAt || plan.startdate)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#8E8D9A" }}>
                                Purchase Date
                              </Typography>
                            </TimelineOppositeContent>

                            <TimelineSeparator>
                              <TimelineDot
                                sx={{
                                  bgcolor: style.color,
                                  boxShadow: `0 0 10px ${style.color}50`,
                                }}
                              />
                              {idx < data.userPlans.length - 1 && <TimelineConnector sx={{ bgcolor: "#F0E7FF" }} />}
                            </TimelineSeparator>

                            <TimelineContent sx={{ py: "12px", px: 2 }}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: "8px",
                                  border: "1px solid #FAF2FF",
                                  bgcolor: "#FCF8FF",
                                }}
                              >
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                  <Typography variant="subtitle1" component="span" sx={{ fontWeight: "bold", color: "#2B2A35" }}>
                                    {plan.planname === "nill" ? "Trial" : plan.planname} Plan
                                  </Typography>
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      padding: "2px 8px",
                                      borderRadius: 4,
                                      backgroundColor: style.bg,
                                      border: style.border,
                                      color: style.color,
                                      fontSize: "10px",
                                      fontWeight: "bold",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {style.label}
                                  </span>
                                </Box>

                                <Typography variant="body2" sx={{ color: "#4E4D59", mb: 0.5 }}>
                                  Validity: <strong>{formatDate(plan.startdate)}</strong> to <strong>{formatDate(plan.enddate)}</strong> ({plan.planduration})
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#4E4D59", mb: 0.5 }}>
                                  Amount Paid: <strong>Rs. {plan.planamount ?? 0}</strong>
                                </Typography>
                                {plan.transactionId && (
                                  <Typography variant="caption" sx={{ display: "block", color: "#8E8D9A", mt: 1 }}>
                                    TXN ID: {plan.transactionId}
                                  </Typography>
                                )}
                              </Box>
                            </TimelineContent>
                          </TimelineItem>
                        );
                      })}
                  </Timeline>
                )}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Typography sx={{ textAlign: "center", color: "#8E8D9A", py: 4 }}>
            Failed to load user details.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
